import { useCallback, useEffect, useRef, useState } from "react";

import { decodeOnboarding, EMPTY_ONBOARDING } from "../decode";
import type { DecodedOnboarding } from "../decode";
import { fetchProfileJson, retrySeeding } from "./api";
import { POLL_INTERVAL_MS, retryDelayMs } from "./helpers";
import type { SeedingAction } from "./helpers";

export interface UseDemoSeedingResult {
  readonly loading: boolean;
  readonly onboarding: DecodedOnboarding;
  readonly requestError: string | null;
  readonly actionError: string | null;
  readonly actionPending: SeedingAction | null;
  readonly refresh: () => Promise<void>;
  readonly retry: () => Promise<void>;
}

/**
 * Reads the profile and keeps reading it while a seed is running.
 *
 * Called from exactly ONE place — `OnboardingProvider` — so both gates share a
 * single request. Mounting it twice would poll twice.
 */
export function useDemoSeeding(): UseDemoSeedingResult {
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState<DecodedOnboarding>(EMPTY_ONBOARDING);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState<SeedingAction | null>(null);
  const mountedRef = useRef(false);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /// The last successfully decoded state, readable from the error path without
  /// making `loadProfile` depend on it and re-create itself every render.
  const onboardingRef = useRef<DecodedOnboarding>(EMPTY_ONBOARDING);
  const failuresRef = useRef(0);

  const clearPoll = useCallback((): void => {
    if (pollTimeoutRef.current !== null) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const loadProfile = useCallback(async (): Promise<void> => {
    clearPoll();
    try {
      const body = await fetchProfileJson();
      const next = body === null ? EMPTY_ONBOARDING : decodeOnboarding(body);
      if (!mountedRef.current) return;
      setOnboarding(next);
      onboardingRef.current = next;
      setRequestError(null);
      setLoading(false);
      failuresRef.current = 0;
      if (next.seeding.status === "provisioning") {
        pollTimeoutRef.current = setTimeout(() => {
          void loadProfile();
        }, POLL_INTERVAL_MS);
      }
    } catch {
      if (!mountedRef.current) return;
      setLoading(false);
      setRequestError("We couldn't load your workspace. Please try again.");
      // Keep polling through a failed read. A seed that was running is still
      // running, and the gate deliberately keeps blocking on it — so giving up
      // here would leave the only recovery a page reload, because the splash's
      // retry POSTs `/retry`, which answers 409 while the seed is healthy.
      //
      // Backed off, because the blocked tab would otherwise put ~80 requests a
      // minute into a server that is already down, and every blocked tab does
      // it — arriving together the moment it comes back.
      if (onboardingRef.current.seeding.status === "provisioning") {
        failuresRef.current += 1;
        pollTimeoutRef.current = setTimeout(() => {
          void loadProfile();
        }, retryDelayMs(failuresRef.current));
      }
    }
  }, [clearPoll]);

  useEffect(() => {
    mountedRef.current = true;
    void loadProfile();
    return (): void => {
      mountedRef.current = false;
      clearPoll();
    };
  }, [clearPoll, loadProfile]);

  const refresh = useCallback(async (): Promise<void> => {
    setActionPending("refresh");
    setActionError(null);
    await loadProfile();
    if (mountedRef.current) setActionPending(null);
  }, [loadProfile]);

  const retry = useCallback(async (): Promise<void> => {
    clearPoll();
    setActionPending("retry");
    setActionError(null);
    try {
      await retrySeeding();
      // The server accepted the restart, so the seed IS running again — record
      // that before re-reading. If the read then fails, the poll below has a
      // `provisioning` status to keep going from; leaving it `failed` would
      // point the only button back at `/retry`, which now answers 409, and the
      // person would need a page reload to escape.
      const restarted: DecodedOnboarding = {
        ...onboardingRef.current,
        completed: false,
        seeding: { status: "provisioning", progress: null },
      };
      onboardingRef.current = restarted;
      if (mountedRef.current) setOnboarding(restarted);
      await loadProfile();
    } catch {
      if (mountedRef.current) {
        setActionError("We couldn't restart workspace setup. Please try again.");
      }
    } finally {
      if (mountedRef.current) setActionPending(null);
    }
  }, [clearPoll, loadProfile]);

  return {
    loading,
    onboarding,
    requestError,
    actionError,
    actionPending,
    refresh,
    retry,
  };
}
